import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Users, Target, Award, CheckCircle } from 'lucide-react';
import { Card, CardContent } from '../components/ui/card';

const About = () => {
  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="bg-gradient-to-br from-blue-50 via-white to-blue-50 py-20">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl font-bold text-gray-900 mb-6">About Dard Satya</h1>
            <p className="text-xl text-gray-600 leading-relaxed">
              A sanctuary of comfort and care for those facing life's most challenging journeys
            </p>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <img 
                src="https://images.unsplash.com/photo-1765896387398-1e1ae8d2eb85" 
                alt="Compassionate conversation"
                className="rounded-3xl shadow-2xl"
              />
            </div>
            <div className="space-y-6">
              <div className="inline-block bg-blue-100 text-blue-700 px-4 py-2 rounded-full text-sm font-medium">
                Our Mission
              </div>
              <h2 className="text-4xl font-bold text-gray-900">Where Comfort Meets Care</h2>
              <p className="text-lg text-gray-600 leading-relaxed">
                Dard Satya was founded on a simple yet profound belief: every person deserves comfort, 
                dignity, and compassionate care, especially during life's most difficult moments.
              </p>
              <p className="text-lg text-gray-600 leading-relaxed">
                We provide holistic palliative care for individuals facing cancer pain, neurological conditions, 
                stroke, traumatic brain injury, dementia, coma, and other terminal illnesses. Our approach goes 
                beyond medical treatment—we care for the whole person: body, mind, and spirit.
              </p>
              <div className="space-y-3">
                {[
                  "Comprehensive pain management",
                  "Emotional and psychological support",
                  "Family counseling and education",
                  "24/7 guidance and care coordination"
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="text-blue-600" size={20} />
                    <span className="text-gray-700">{item}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values Section */}
      <section className="py-20 bg-gradient-to-br from-blue-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">Our Core Values</h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              The principles that guide every aspect of our care
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                icon: Heart,
                title: "Compassion",
                desc: "Every interaction is guided by genuine empathy and understanding"
              },
              {
                icon: Users,
                title: "Family-Centered",
                desc: "We support not just patients, but entire families through their journey"
              },
              {
                icon: Target,
                title: "Dignity",
                desc: "Preserving the dignity and autonomy of every individual we serve"
              },
              {
                icon: Award,
                title: "Excellence",
                desc: "Commitment to the highest standards of palliative care"
              }
            ].map((value, index) => (
              <Card key={index} className="border-none shadow-lg hover:shadow-xl transition-all text-center">
                <CardContent className="p-8">
                  <div className="bg-blue-100 w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                    <value.icon className="text-blue-600" size={28} />
                  </div>
                  <h3 className="text-xl font-semibold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600">{value.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* What We Do */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="text-center mb-12">
              <h2 className="text-4xl font-bold text-gray-900 mb-4">What We Do</h2>
              <p className="text-lg text-gray-600">
                Creating awareness and providing comprehensive support
              </p>
            </div>
            
            <div className="space-y-6">
              <Card className="border-l-4 border-l-blue-600 shadow-md">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Advice & Consultation</h3>
                  <p className="text-gray-600">
                    We provide expert guidance on pain management, symptom control, and care planning. 
                    Our team is available to answer questions and help families make informed decisions.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-blue-600 shadow-md">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Holistic Palliative Care</h3>
                  <p className="text-gray-600">
                    Our comprehensive approach addresses physical symptoms, emotional distress, 
                    spiritual concerns, and practical needs. We treat the whole person, not just the illness.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-blue-600 shadow-md">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Support for All Terminal Illnesses</h3>
                  <p className="text-gray-600">
                    Whether facing cancer, stroke, traumatic brain injury, dementia, coma, brain death, 
                    or other life-limiting conditions—we provide specialized care tailored to each situation.
                  </p>
                </CardContent>
              </Card>

              <Card className="border-l-4 border-l-blue-600 shadow-md">
                <CardContent className="p-6">
                  <h3 className="text-xl font-semibold text-gray-900 mb-2">Family Support & Education</h3>
                  <p className="text-gray-600">
                    We recognize that families need support too. We offer caregiver training, 
                    emotional support, and resources to help families navigate their loved one's illness.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-blue-500 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-4xl font-bold mb-6">Let Us Walk With You</h2>
            <p className="text-xl mb-8 text-blue-50">
              We're here to provide the comfort, care, and support you and your loved ones deserve.
            </p>
            <Link to="/contact">
              <button className="bg-white text-blue-600 hover:bg-blue-50 px-8 py-4 text-lg font-medium rounded-full shadow-lg transition-all">
                Get in Touch
              </button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;
